import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { downloadFile } from "common/helpers/utils";
import AHBoolean from "common/values/boolean/boolean";
import Guid from "common/values/guid/guid";
import DocumentAPIService from "documents/entities/document/api/document-api-service";
import Document from "documents/entities/document/document";
import DocumentSelector from "documents/view/document-selector";
import DocumentUploadOverlay from "documents/view/document-upload-overlay";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/session/session-context";
import CommentThread from "work/entities/comment-thread/comment-thread";
import { ProposalField } from "work/entities/proposal/proposal";
import FieldRedline, { FieldRedlineArray, } from "work/entities/proposal/redlining/field-redline";
import ProposalRedline from "work/entities/proposal/redlining/proposal-redline";
import DocumentRedlineSelection
  from "work/entities/proposal/redlining/view/redline-field-inputs/document-redline-selection";
import SwitchRedline from "work/entities/proposal/redlining/view/redline-field-inputs/switch-redline";
import ProposalIssues, { ProposalIssueLevel } from "work/values/proposal-issues/proposal-issues";
import WorkDocument, { WorkDocumentType, } from "work/values/work-document/work-document";
import CommentsButton from "work/entities/comment-thread/view/comments-button";
import { ProposalFieldName } from "work/values/constants";
import { Masked } from "../../../../work-agreement/work-agreement";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(
  Grid,
  {
    shouldForwardProp: (prop) => prop !== "waived",
  }
)<{ waived: boolean }>(
  ({ waived }) => ({
    flexWrap: "nowrap",
    minHeight: "20rem",
    opacity: waived ? 0.25 : 1,
    pointerEvents: waived ? "none" : "auto",
  }));
const DocumentSelectorContainer = styled(Grid)(
  () => ({
    display: "flex",
    overflowY: "auto",
  }));
const SelectedDocsList = styled(DocumentRedlineSelection)(
  ({ theme }) => ({
    [theme.breakpoints.down("lg")]: {
      marginTop: theme.spacing(2),
    },
    maxHeight: "60vh",
    marginLeft: theme.spacing(2),
  }));
const HeadControls = styled("div")(
  ({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    paddingLeft: theme.spacing(2),
  }));
const Uploader = styled(DocumentUploadOverlay)(
  () => ({
    backgroundColor: "rgba(250, 250, 250, 0.5)",
    backdropFilter: "blur(5px) saturate(200%)",
    position: "absolute",
    top: 0,
    left: 0,
    margin: "1rem",
    width: "calc(100% - 2rem)",
    height: "calc(100% - 2rem)",
    zIndex: 9999,
  }));
const Change = styled(
  "div",
  { shouldForwardProp: (prop) => prop !== 'active' }
)<{ active: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s infinite` : "none",
    border: active ? "1px solid" : 0,
    borderColor: theme.palette.primary.main,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
  }));
const IssueContainer = styled(
  "div",
  { shouldForwardProp: (prop) => prop !== 'active' }
)<{ active?: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
    margin: active ? theme.spacing(1) : 'initial',
    marginLeft: 0,
    marginTop: active ? 0 : theme.spacing(1),
    padding: active ? theme.spacing(1) : "initial",
    borderRadius: theme.shape.borderRadius,
  }));
const DocumentGrid = styled(Grid)(
  () => ({
    flex: 1,
    overflowY: "scroll"
  }));

type ConflictsTabProps = {
  isOpen: boolean;
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  commentThreads: CommentThread[];
  disableEditing?: boolean;
  onProposalRedlineChange: (
    newRedline: ProposalRedline,
    traversalFieldOverride?: ProposalField | null
  ) => void;
  onTraverseToNewField: (fieldInfo: ProposalField | undefined) => void;
  commentsToolTip: string;
  onCommentsClicked: (field: ProposalField, name?: string) => void;
};

const ConflictsTab = (props: Readonly<ConflictsTabProps>) => {
  const {
    isOpen,
    proposalRedline,
    activeReviewField,
    commentThreads,
    disableEditing,
    onProposalRedlineChange,
    onTraverseToNewField,
    commentsToolTip,
    onCommentsClicked,
  } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(
    null
  );
  const [documentIdToReplace, setDocumentIdToReplace] =
    React.useState<Guid | null>(null);
  const [creatingDocumentFromTemplate, setCreatingDocumentFromTemplate] =
    React.useState<Guid | null>(null);

  const issues = React.useMemo(
    () => props.issues?.entries.filter(issue => [
      ProposalField.Conflicts,
      ProposalField.ConflictsDocument(issue.field.id ?? Guid.empty),
      ProposalField.WaiveConflictsCheck
    ].includes(issue.field)),
    [props.issues?.entries]
  );

  const session = useSession();

  async function handleDownloadDocumentById(
    documentId?: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event?.stopPropagation();
    if (!documentId) return;

    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const conflictsTemplate = await documentApiService.downloadDocument(
        documentId
      );
      await downloadFile(conflictsTemplate);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        "Failed to download conflicts template. Please try again",
        { variant: "error" }
      );
    } finally {
      setIsDownloadingFile(null);
    }
  }

  function handleDocumentRedlineChanged(
    newDocumentsRedline: FieldRedlineArray<WorkDocument>,
    traversalFieldOverride?: ProposalField
  ) {
    let updatedProposalRedline =
      proposalRedline.updateConflictsDocumentsRedline(newDocumentsRedline);
    if (Masked.isMasked(updatedProposalRedline.conflictsCheckWaived))
      throw new Error("Conflicts Check Waived cannot be a Masked Contract");

    if (
      newDocumentsRedline.redlines.some(
        (documentRedline) => documentRedline.currentEntry
      ) &&
      !updatedProposalRedline.conflictsCheckWaived.currentEntry?.isEqualTo(
        new AHBoolean(false)
      )
    ) {
      updatedProposalRedline =
        updatedProposalRedline.updateConflictsCheckWaivedRedline(
          updatedProposalRedline.conflictsCheckWaived.edit(new AHBoolean(false))
        );
    }
    onProposalRedlineChange(
      updatedProposalRedline,
      traversalFieldOverride
    );
    onCommentsClicked(
      ProposalField.Conflicts,
      undefined
    );
  }

  function handleDocumentSelectionChanged(
    selectedDocs: Document[],
    deselectedDocs: Document[]
  ) {
    if (!selectedDocs.length && !deselectedDocs.length) return;
    let updatedProposalRedline = proposalRedline.clone();
    if (Masked.isMasked(updatedProposalRedline.conflictsCheckWaived)) {
      throw new Error("Conflicts Check Waived cannot be a Masked Contract");
    }

    if (selectedDocs.length &&
      !updatedProposalRedline.conflictsCheckWaived.currentEntry?.isEqualTo(new AHBoolean(false))) {
      updatedProposalRedline = updatedProposalRedline.updateConflictsCheckWaivedRedline(
        updatedProposalRedline.conflictsCheckWaived.edit(new AHBoolean(false))
      );
    }

    for (const selectedDoc of selectedDocs) {
      if (Masked.isMasked(updatedProposalRedline.conflictsDocuments)) {
        throw new Error("Conflicts Documents cannot be a Masked Contract");
      }

      const workDocument = WorkDocument.fromDocument(selectedDoc, WorkDocumentType.Conflicts);
      if (
        !updatedProposalRedline.conflictsDocuments.redlines.some((document) =>
          document.currentEntry?.isEqualTo(workDocument)
        )
      ) {
        updatedProposalRedline =
          updatedProposalRedline.updateConflictsDocumentsRedline(
            updatedProposalRedline.conflictsDocuments.addEntry(workDocument)
          );
      }
    }

    for (const deselectedDoc of deselectedDocs) {
      if (Masked.isMasked(updatedProposalRedline.conflictsDocuments)) {
        throw new Error("Conflicts Documents cannot be a Masked Contract");
      }

      const redlineId = updatedProposalRedline.conflictsDocuments.redlines.find(
        (documentRedline) =>
          documentRedline.currentEntry?.id?.isEqualTo(deselectedDoc.id) ||
          (!documentRedline.currentEntry &&
            documentRedline.revisedEntry?.id?.isEqualTo(deselectedDoc.id))
      )?.field.id;

      if (!redlineId) {
        continue;
      }

      updatedProposalRedline =
        updatedProposalRedline.updateConflictsDocumentsRedline(
          updatedProposalRedline.conflictsDocuments.removeEntryByFieldId(
            redlineId
          )
        );
    }

    onProposalRedlineChange(
      updatedProposalRedline,
      null
    );
    onCommentsClicked(
      ProposalField.Conflicts,
      undefined
    );
  }

  function renderDocumentSelector() {
    if (Masked.isMasked(proposalRedline.conflictsDocuments)) {
      throw new Error("Conflicts Documents cannot be a Masked Contract");
    }
    let selectedTemplateIds: Array<Guid> = [];
    for (const documentRedline of proposalRedline.conflictsDocuments.redlines) {
      if (documentRedline.currentEntry) {
        documentRedline.currentEntry.templateIds.forEach((templateId) => {
          selectedTemplateIds.push(templateId);
        });
      }
    }

    return (
      <DocumentSelector
        documentType="conflicts"
        selectedDocumentIds={proposalRedline.conflictsDocuments.currentIds}
        selectedTemplateIds={selectedTemplateIds}
        onDocumentSelectionChanged={handleDocumentSelectionChanged}
        onCreateDocumentFromTemplate={handleBeginCreateDocumentFromTemplate}
      />
    );
  }

  function handleConflictsCheckWaiverChanged(
    newRedline: FieldRedline<AHBoolean>
  ) {
    try {
      onProposalRedlineChange(
        proposalRedline.updateConflictsCheckWaivedRedline(newRedline),
        ProposalField.WaiveConflictsCheck
      );
      onCommentsClicked(
        ProposalField.Conflicts,
        undefined
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to update conflicts check waiver.",
        {
          variant: "error",
        }
      );
    }
  }

  async function handleBeginCreateDocumentFromTemplate(
    templateId: Guid | undefined | null
  ) {
    if (!templateId) return;
    setCreatingDocumentFromTemplate(templateId);
    await handleDownloadDocumentById(templateId);
  }

  async function handleBeginReplaceDocument(
    documentId: Guid | undefined | null
  ) {
    if (!documentId) throw new Error("Document ID must be present to replace document");
    setDocumentIdToReplace(documentId);
    await handleDownloadDocumentById(documentId);
  }

  function handleConflictsDocumentAdded(newDocument: Document) {
    if (Masked.isMasked(proposalRedline.conflictsDocuments)) {
      throw new Error("Conflicts Documents cannot be a Masked Contract");
    }

    const newWorkDocument = WorkDocument.fromDocument(
      newDocument,
      WorkDocumentType.Conflicts,
      documentIdToReplace ?? undefined
    );

    let updatedProposalRedline = proposalRedline.clone();
    if (Masked.isMasked(updatedProposalRedline.conflictsDocuments) ||
      Masked.isMasked(updatedProposalRedline.conflictsCheckWaived)) {
      throw new Error("Conflicts Documents cannot be a Masked Contract");
    }

    try {
      if (documentIdToReplace) {
        updatedProposalRedline =
          proposalRedline.updateConflictsDocumentsRedline(
            proposalRedline.conflictsDocuments.replaceEntryById(
              documentIdToReplace,
              newWorkDocument
            )
          );
      } else if (!proposalRedline.conflictsDocuments.redlines
        .some((document) => document.currentEntry?.isEqualTo(newWorkDocument))
      ) {
        updatedProposalRedline =
          proposalRedline.updateConflictsDocumentsRedline(proposalRedline.conflictsDocuments.addEntry(newWorkDocument));
      }

      if (Masked.isMasked(updatedProposalRedline.conflictsCheckWaived)) {
        throw new Error("Conflicts Check Waived cannot be a Masked Contract");
      }

      if (
        !updatedProposalRedline.conflictsCheckWaived.currentEntry?.isEqualTo(
          new AHBoolean(false)
        )
      ) {
        updatedProposalRedline =
          updatedProposalRedline.updateConflictsCheckWaivedRedline(
            updatedProposalRedline.conflictsCheckWaived.edit(
              new AHBoolean(false)
            )
          );
      }

      onProposalRedlineChange(
        updatedProposalRedline,
        null
      );
      onCommentsClicked(
        ProposalField.Conflicts,
        undefined
      )
      setDocumentIdToReplace(null);
      setCreatingDocumentFromTemplate(null);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to add conflicts document.",
        {
          variant: "error",
        }
      );
    }
  }

  if (!isOpen) return null;
  if (Masked.isMasked(proposalRedline.conflictsCheckWaived) ||
    Masked.isMasked(proposalRedline.conflictsDocuments)) {
    return "You do not have permission to view contracts.";
  }

  const newlyAddedConflictsDocuments = proposalRedline.conflictsDocuments.redlines
    .some((redline) => redline.isNewlyAdded);
  return (
    <>
      <HeadControls>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer
            active={
              activeReviewField?.name === ProposalFieldName.WaiveConflictsCheck
            }
          >
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.key}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <Change
          active={
            activeReviewField?.isEqualTo(ProposalField.WaiveConflictsCheck) ?? false
          }
        >
          <SwitchRedline
            disabled={disableEditing}
            boolRedline={proposalRedline.conflictsCheckWaived}
            promptTitleText={
              newlyAddedConflictsDocuments ?
                "Remove Any Newly-Added Documents?" :
                undefined
            }
            promptMessageText={
              newlyAddedConflictsDocuments ?
                "Waiving conflicts will remove any newly-added documents. Are you sure you want to proceed?" :
                undefined
            }
            okButtonText={
              newlyAddedConflictsDocuments ?
                "Remove" :
                undefined
            }
            unsetLabel="Not Waiving Conflicts Check"
            setLabel="Waiving Conflicts Check"
            onBoolRedlineChange={(newRedline) => {
              try {
                handleConflictsCheckWaiverChanged(newRedline);
              } catch (error) {
                console.error(error);
                enqueueSnackbar(
                  "Failed to update conflicts check waiver.",
                  {
                    variant: "error",
                  }
                );
              }
            }}
          />
        </Change>
        <CommentsButton
          commentThreads={commentThreads}
          field={ProposalField.Conflicts}
          isActive={activeReviewField?.isEqualTo(ProposalField.Conflicts)}
          toolTip={commentsToolTip}
          onCommentsClicked={
            onCommentsClicked ? () => {
              onCommentsClicked(
                ProposalField.Conflicts
              );
            } : undefined
          }
        />
      </HeadControls>
      <ListContainer
        waived={
          proposalRedline.conflictsCheckWaived.currentEntry?.value ??
          proposalRedline.conflictsCheckWaived.revisedEntry?.value ??
          false
        }
        container
        direction="row"
      >
        {!disableEditing && (
          <>
            <DocumentSelectorContainer>
              {renderDocumentSelector()}
            </DocumentSelectorContainer>
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          </>
        )}
        <DocumentGrid size={4}>
          <SelectedDocsList
            workDocumentType={WorkDocumentType.Conflicts}
            documentsRedline={proposalRedline.conflictsDocuments}
            downloadingFileId={isDownloadingFile}
            commentThreads={commentThreads}
            disableEditing={disableEditing}
            activeReviewField={activeReviewField}
            commentsToolTip={commentsToolTip}
            onCommentsClicked={onCommentsClicked}
            onDownload={handleDownloadDocumentById}
            onDocumentRedlineChange={(newRedline) => {
              try {
                handleDocumentRedlineChanged(newRedline);
              } catch (error) {
                console.error(error);
                enqueueSnackbar(
                  "Failed to update conflicts document.",
                  {
                    variant: "error",
                  }
                );
              }
            }}
            onBeginReplaceDocument={handleBeginReplaceDocument}
            onDocumentClicked={(documentId: Guid) => {
              onTraverseToNewField?.(
                ProposalField.ConflictsDocument(documentId)
              );
            }}
            onTraverseToNewField={onTraverseToNewField}
          />
        </DocumentGrid>
      </ListContainer>
      {(creatingDocumentFromTemplate || documentIdToReplace) && (
        <Uploader
          session={session}
          documentType={WorkDocumentType.Conflicts}
          templateId={creatingDocumentFromTemplate ?? undefined}
          onDocumentUploaded={handleConflictsDocumentAdded}
          onClose={() => {
            setCreatingDocumentFromTemplate(null);
            setDocumentIdToReplace(null);
          }}
        />
      )}
    </>
  );
};

export default ConflictsTab;
